"use client";
import { useEffect } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import StyledComponentsRegistry from "../components/StyledComponentsRegistry";
import { defaultTheme } from "../styles/defaultTheme";
import { Fonts, shadow, focusEffect } from "../styles/globalsStyles";

// Error boundaries must be Client Components

export default function GlobalError({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    // global-error must include html and body tags
    <html lang="he" dir="rtl">
      <head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Assistant:wght@200;300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </head>
      <body>
        <StyledComponentsRegistry>
          <ResetStyle />
          <Wrapper>
            <Logo src="/assets/buisnessLogo.png" alt="רינה הלוי" />
            <Card>
              <Badge>!</Badge>
              <Title>אופס, משהו השתבש</Title>
              <Subtitle>
                נתקלנו בתקלה בלתי צפויה. אפשר לנסות שוב, ואם זה חוזר על עצמו
                נשמח שתדווחו לנו.
              </Subtitle>
              {error?.digest && <Digest>{error.digest}</Digest>}
              <Actions>
                {reset && (
                  <RetryButton onClick={() => reset()}>נסה שוב</RetryButton>
                )}
                <HomeLink href="/">חזרה לדף הבית</HomeLink>
              </Actions>
            </Card>
          </Wrapper>
        </StyledComponentsRegistry>
      </body>
    </html>
  );
}

const ResetStyle = createGlobalStyle`
  html, body {
    margin: 0;
    padding: 0;
    height: 100%;
  }
  * {
    box-sizing: border-box;
  }
`;

const popIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const Wrapper = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 2rem 1rem;
  background-color: ${defaultTheme.tertiaryColor};
`;

const Logo = styled.img`
  width: 90px;
  height: auto;
  object-fit: contain;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 20px;
  ${shadow}
  padding: 2.5rem 2rem;
  max-width: 480px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  animation: ${popIn} 0.4s ease-out;
`;

const Badge = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${defaultTheme.secondaryColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  ${Fonts.xLarge}
  font-weight: 700;
`;

const Title = styled.h1`
  ${Fonts.large}
  font-weight: 700;
  color: ${defaultTheme.primaryColor};
  margin: 0;
`;

const Subtitle = styled.p`
  ${Fonts.small}
  color: ${defaultTheme.primaryColor};
  opacity: 0.75;
  margin: 0;
`;

const Digest = styled.code`
  ${Fonts.xxSmall}
  color: ${defaultTheme.primaryColor};
  opacity: 0.5;
  direction: ltr;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const RetryButton = styled.button`
  background-color: ${defaultTheme.primaryColor};
  border: 1px solid ${defaultTheme.primaryColor};
  padding: 0.85rem 1.75rem;
  color: white;
  border-radius: 8px;
  ${shadow}
  transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;
  ${Fonts.small}

  &:hover {
    cursor: pointer;
    color: white;
    background-color: ${defaultTheme.secondaryColor};
    border-color: ${defaultTheme.secondaryColor};
  }

  ${focusEffect}
`;

const HomeLink = styled.a`
  background-color: white;
  border: 1px solid ${defaultTheme.secondaryColor};
  padding: 0.85rem 1.75rem;
  color: ${defaultTheme.primaryColor};
  border-radius: 8px;
  ${shadow}
  transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;
  ${Fonts.small}
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: pointer;
    color: white;
    background-color: ${defaultTheme.secondaryColor};
    border-color: ${defaultTheme.secondaryColor};
  }

  ${focusEffect}
`;
